import * as React from 'react';
import { FormattedDateTime } from '../FormattedDate';
import { joinClasses } from '../../Utils/className.util';
import CaseChatEntryOrder from './CaseChatEntryOrder';
import CaseChatEntryFile from './CaseChatEntryFile';
import CaseChatEntryDocumentation from './CaseChatEntryDocumentation';
import { CaseEntry, User } from '../../graphql/graphql';
import UserAvatarImage from '../UserAvatar/UserAvatarImage';
import EscapeWithLines from '../../Modules/Base/Components/EscapeWithLines/EscapeWithLines';
import { UserAvatarImageType } from '../../Enums/UserAvatarImageType.enum';
import { useMemo } from 'react';
import Text from '../Base/Text/Text';

interface CaseChatEntryProps {
	entry: CaseEntry;
	myUserId: string;
	parentId?: string;
	child?: User;
	beforeChange: () => void;
	afterChange: () => void;
	key: string;
}

export interface Order {
	id: string;
	number: string;
	price: Price;
	orderLog: {
		type: string;
		value: string;
	};
	orderItems: [OrderItem];
}

export interface OrderItem {
	id: string;
	name: string;
	price: Price;
}

export interface Price {
	formattedPrice: string;
	priceWithVatRaw: string;
	currency: string;
}

export default function CaseChatEntry(props: CaseChatEntryProps) {
	const { entry, myUserId, parentId, child, beforeChange, afterChange } = props;
	const { id, createdAt, message, type, senderId, extra, order } = entry;
	const avatarUser = child && senderId === parentId ? child : undefined;

	const isSeenByPatient: boolean = useMemo(() => {
		return (
			entry.caseEntryMembers?.find(item => item?.user?.isPatient)?.seen ?? false
		);
	}, [entry]);

	return (
		<div
			className={joinClasses([
				'chat-message',
				'd-flex',
				senderId === myUserId ? 'flex-row-reverse' : undefined,
				!senderId ? 'info' : undefined,
			])}>
			<div
				className={joinClasses([
					'message-text',
					'case-detail-chat-window__message-wrapper',
					'flex-shrink-1',
					'rounded',
					'py-2',
					senderId && senderId !== myUserId ? 'ml-4 pr-4 pl-5' : '',
					senderId && senderId === myUserId ? 'mr-4 pl-4 pr-5' : '',
					!senderId ? 'message-text-info px-4' : '',
				])}>
				{senderId && (
					<div className='chat-profile-image'>
						<UserAvatarImage
							userId={avatarUser?.id ?? senderId}
							profileImageUrl={avatarUser?.profileImageUrl}
							type={UserAvatarImageType.BACKGROUND}
							customClass={'profile-photo profile-photo__message'}
							createdAt={
								avatarUser
									? avatarUser.avatarFile?.createdAt
									: entry?.sender?.avatarFile?.createdAt
							}
						/>
					</div>
				)}
				<div className='chat-message__content'>
					{!senderId && (
						<div className='chat-message__time'>
							<FormattedDateTime datetime={createdAt} />
						</div>
					)}
					{(!senderId || type === 'm') && message && (
						<div className='chat-message__message'>
							<EscapeWithLines text={message} />
						</div>
					)}
					{(type === 'p' || type === 'f') && (
						<CaseChatEntryFile
							entry={entry}
							beforeChange={beforeChange}
							afterChange={afterChange}
						/>
					)}
					{type === 'order' && (
						<div className='chat-message__message'>
							<CaseChatEntryOrder
								order={order}
								entryId={id}
								beforeChange={beforeChange}
								afterChange={afterChange}
							/>
						</div>
					)}
					{type === 'documentation' && (
						<div className='chat-message__message'>
							<CaseChatEntryDocumentation entry={entry} />
						</div>
					)}
					{senderId && (
						<div className='chat-message__time'>
							<FormattedDateTime datetime={createdAt} />
						</div>
					)}
					{isSeenByPatient && senderId && senderId === myUserId && (
						<Text className='chat-message__read' tx='base.read' />
					)}
				</div>
			</div>
		</div>
	);
}
